import { OidcClient } from "@axa-fr/oidc-client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Prod_Custom } from "../Domains_Obj/Prod_Custom";
import { PreProd_Custom } from "../Domains_Obj/PreProd_Custom";
import { QA_Custom } from "../Domains_Obj/QA_Custom";
import { SESTest_Custom } from "../Domains_Obj/SesTest_Custom";

export const useIdentityServer = process.env.REACT_APP_USE_IDENTITY_SERVER
  ? process.env.REACT_APP_USE_IDENTITY_SERVER.toLowerCase() === "true"
  : false;

/**
 * Normalizes a URL by collapsing any duplicate forward slashes in the path
 * portion while preserving the protocol double slash (e.g. https://).
 * Guards against stray leading/trailing slashes on env var values being
 * injected by build pipelines, which can produce URLs like https://host//identity.
 *
 * Examples:
 *   normalizeUrl("https://host//identity/path")  → "https://host/identity/path"
 *   normalizeUrl("https://host/portal//callback") → "https://host/portal/callback"
 *   normalizeUrl("https://host/path/")            → "https://host/path/"  (unchanged)
 */
export const normalizeUrl = (url) => {
  if (!url) return url;
  // Replace any run of 2+ consecutive slashes that is NOT immediately preceded
  // by a colon (which would be the ":" in "https:") with a single slash.
  return url.trim().replace(/([^:])\/\/+/g, "$1/");
};

const foundIdentityRedirectUrlElement = document.getElementById(
  "IdentityRedirectUrl"
);
const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href");
// Normalize the authority value once at definition so all downstream string
// comparisons are case-insensitive without scattering .toLowerCase() calls
// throughout. The leading-slash and double-slash guards in normalizeUrl handle
// the path-prefix injection that some build pipelines add (e.g. "/Identity").
const authority = process.env.REACT_APP_OPEN_ID_AUTHORITY?.toLowerCase();

const getPortalBaseUrl = () => {
  let redirectUrl =
    foundIdentityRedirectUrlElement && foundIdentityRedirectUrlElement.value
      ? foundIdentityRedirectUrlElement.value
      : baseUrl;

  if (
    !window.location.href.toLowerCase().includes(authority) &&
    redirectUrl.indexOf("http://") !== 0 &&
    redirectUrl.indexOf("https://") !== 0
  ) {
    redirectUrl = window.location.protocol + "//" + window.location.host;
    if (process.env.REACT_APP_API_URL) {
      // Ensure exactly one slash between host and the API path prefix regardless
      // of whether the env var starts with a slash or not.
      redirectUrl = normalizeUrl(redirectUrl + "/" + process.env.REACT_APP_API_URL);
    }

    if (redirectUrl[redirectUrl.length - 1] !== "/") {
      redirectUrl += "/";
    }
  }
  return redirectUrl;
};

const getIdentityUrl = () => {
  let authorityAbsoluteUrl = baseUrl;
  if (window.location.href.toLowerCase().includes(authority)) {
    // If the authority is already an absolute URL use it directly (likely an override).
    if (
      authority.indexOf("http://") === 0 ||
      authority.indexOf("https://") === 0
    ) {
      authorityAbsoluteUrl = authority;
    } else {
      // Build an absolute URL from the current host + the authority path.
      // normalizeUrl collapses any // that arise when `authority` has a leading
      // slash injected by the build pipeline (e.g. "/identity" → "//identity").
      authorityAbsoluteUrl = normalizeUrl(
        window.location.protocol +
        "//" +
        window.location.host +
        "/" +
        authority
      );
    }
  } else {
    if (
      authority.indexOf("http://") === 0 ||
      authority.indexOf("https://") === 0
    ) {
      authorityAbsoluteUrl = authority;
    }

    if (
      authorityAbsoluteUrl.indexOf("http://") !== 0 &&
      authorityAbsoluteUrl.indexOf("https://") !== 0
    ) {
      // Same guard as above — normalizeUrl removes any double slashes introduced
      // by a leading slash on `authority` from the build pipeline.
      authorityAbsoluteUrl = normalizeUrl(
        window.location.protocol +
        "//" +
        window.location.hostname +
        "/" +
        authority
      );
    }
  }
  return authorityAbsoluteUrl;
};

export const getIdentityServerConfiguration = () => {
  let redirectUrl = getPortalBaseUrl();
  let authorityAbsoluteUrl = getIdentityUrl();

  // getIdentityUrl() and getPortalBaseUrl() each apply normalizeUrl() at their
  // construction points, so the values arriving here are already clean.
  const oidcConfig = {
    authority: authorityAbsoluteUrl,
    client_id: process.env.REACT_APP_OPEN_ID_CLIENT_ID,
    client_secret: process.env.REACT_APP_OPEN_ID_CLIENT_SECRET,
    redirect_uri: redirectUrl + "authentication/callback",
    silent_redirect_uri: redirectUrl + "authentication/silent-callback",
    scope: "openid profile roles address ses user_info",
    preload_user_info: true,
    token_renew_mode: "access_token_invalid",
  };

  // Unless the customizations gets more complex and includes a lot of localization this is a quick
  // way to load the customizations without having to call a backend API. If a duplicate domain exists
  // between the environments, we'll still grab the first one which would prioritize higher environments
  let customizations = [
    ...Prod_Custom,
    ...PreProd_Custom,
    ...QA_Custom,
    ...SESTest_Custom,
  ];
  const foundCustomization = customizations.find(
    (x) => x.domainName?.toLowerCase() === window.location.host.toLowerCase()
  );

  if (foundCustomization?.data) {
    // Since we're loading the customizations, we'll go ahead and just set the custom page title here.
    // This doesn't really make sense to be here but we'll do it for now until the customizations get more complex
    if (foundCustomization.data.layout_title) {
      document.title = foundCustomization.data.layout_title;
    }

    if (foundCustomization.data.acr_values) {
      oidcConfig.extras = { acr_values: foundCustomization.data.acr_values };
    }
  }

  return oidcConfig;
};

export const hasRole = (token, roleName) => {
  if (
    (Array.isArray(token.role) &&
      token.role.find((e) => e.toUpperCase() === roleName)) ||
    (!Array.isArray(token.role) && token.role?.toUpperCase() === roleName)
  ) {
    return true;
  }
  return false;
};

export const getUserRole = (token) => {
  let role = "USER";

  if (hasRole(token, "ADMINISTRATOR")) {
    role = "ADMINISTRATOR";
  }

  if (hasRole(token, "SES ADMINISTRATOR")) {
    role = "SES ADMINISTRATOR";
  }

  if (hasRole(token, "SYSTEMADMINISTRATOR")) {
    role = "SYSTEMADMINISTRATOR";
  }

  return role;
};

export const getUserSession = (token) => {
  let notifyFlag = false;
  let createUserFlag = false;

  if (hasRole(token, "ENSACCESS")) {
    notifyFlag = true;
  }

  if (hasRole(token, "CREATEUSERS")) {
    createUserFlag = true;
  }

  return {
    ...token,
    HasNotify: notifyFlag,
    CreateUserFlag: createUserFlag,
  };
};

export const getDefaultLandingPage = (token, userEmails) => {
      let isAdmin = hasRole(token, "ADMINISTRATOR") ||
                    hasRole(token, "SES ADMINISTRATOR") ||
                    hasRole(token, "SYSTEMADMINISTRATOR");

  switch (token?.registration_status) {
    case "PreRegistrationStarted":
      return "/EULA";
    case "InvitationStarted":
      return "/EULA";
    case "PasswordRecovery":
      return "/password-change-preRegistration";
    case "NPIComplete":
      return "/pre-registration";
    case "ContactDetailsComplete":
    case "ExperionIDPFailed":
      return "/experian-sso";
    case "Migration":
      var npi = token?.IDs.Where((x) => x.IDType == 0).FirstOrDefault();
      if (npi == null) {
        return "/dashboard";
      }
      return "/no-direct-email";
    default:
      if (isAdmin) {
        return "/dashboard";
      } else {
        if (Array.isArray(userEmails) && userEmails.length > 0) {
          return "/webmail";
        } else {
          return "/no-direct-email";
        }
      }
  }
};

const defaultConfigurationName = "default";

const defaultIsAuthenticated = (getOidc, configurationName) => {
  let isAuthenticated = false;

  if (useIdentityServer) {
    const oidc = getOidc(configurationName);
    if (oidc) {
      isAuthenticated = getOidc(configurationName).tokens != null;
    }
  }
  return isAuthenticated;
};

export const usePortalAuthentication = (
  configurationName = defaultConfigurationName
) => {
  const getOidc = OidcClient.get;
  const [isAuthenticated, setIsAuthenticated] = useState(
    defaultIsAuthenticated(getOidc, configurationName)
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (useIdentityServer) {
      let isMounted = true;
      const oidc = getOidc(configurationName);
      setIsAuthenticated(defaultIsAuthenticated(getOidc, configurationName));
      const newSubscriptionId = oidc.subscribeEvents((name, data) => {
        if (
          name === OidcClient.eventNames.logout_from_another_tab ||
          name === OidcClient.eventNames.logout_from_same_tab ||
          name === OidcClient.eventNames.token_aquired
        ) {
          if (isMounted) {
            setIsAuthenticated(
              defaultIsAuthenticated(getOidc, configurationName)
            );
          }
        }
      });
      return () => {
        isMounted = false;
        oidc.removeEventSubscription(newSubscriptionId);
      };
    }
  }, [configurationName]);

  const login = (
    callbackPath = undefined,
    extras = null,
    silentLoginOnly = false
  ) => {
    if (useIdentityServer) {
      return getOidc(configurationName).loginAsync(
        getPortalBaseUrl(),
        extras,
        false,
        undefined,
        silentLoginOnly
      );
    } else {
      return navigate("/");
    }
  };

  const logout = (callbackPath = undefined, extras = null) => {
    if (useIdentityServer) {
      return getOidc(configurationName).logoutAsync(getPortalBaseUrl(), extras);
    } else {
      return navigate("/signout");
    }
  };

  return {
    login,
    logout,
    isAuthenticated,
  };
};
